/**
 * useHousingStatus — decision-grade status hook.
 * Provides user-facing labels from single source of truth.
 * Do NOT modify STATUS_MAP — it is the display layer over backend states.
 */
import { useState, useEffect } from "react";
import client from "@/api/client";

// ── Display layer: backend state → decision-grade user label ───────────────
const STATUS_MAP = {
  draft:             { review_status: "Action Required", stage: "Internal",     readiness: "Early Stage" },
  calculated:        { review_status: "In Review",       stage: "Internal",     readiness: "Early Stage" },
  structured:        { review_status: "In Review",       stage: "Internal",     readiness: "Early Stage" },
  submitted:         { review_status: "In Review",       stage: "External",     readiness: "Early Stage" },
  requires_revision: { review_status: "Action Required", stage: "Internal",     readiness: "Early Stage" },
  rejected:          { review_status: "Action Required", stage: "Internal",     readiness: "Early Stage" },
  verified:          { review_status: "Verified",        stage: "Verification", readiness: "Finance Ready (Subject to Validation)" },
  Assessed:          { review_status: "In Review",       stage: "Internal",     readiness: "Early Stage" },
};

// ── Rule: "Finance Ready" only when verified + verification stage ────────────
function deriveBankable(fields) {
  return fields.review_status === "Verified" && fields.stage === "Verification";
}

// ── Visual config per review_status ────────────────────────────────────────
const STATUS_VISUAL = {
  "Action Required": {
    badge:   "bg-amber-100 text-amber-800 border border-amber-300",
    dot:     "bg-amber-500",
    dot_dim: "bg-amber-200",
    ring:    "border-amber-300 bg-amber-50",
    icon:    "!",
  },
  "In Review": {
    badge:   "bg-blue-100 text-blue-800 border border-blue-200",
    dot:     "bg-blue-500",
    dot_dim: "bg-blue-200",
    ring:    "border-blue-200 bg-blue-50",
    icon:    "~",
  },
  "Verified": {
    badge:   "bg-emerald-100 text-emerald-800 border border-emerald-300",
    dot:     "bg-emerald-500",
    dot_dim: "bg-emerald-200",
    ring:    "border-emerald-300 bg-emerald-50",
    icon:    "✓",
  },
};

// ── Readiness config ────────────────────────────────────────────────────────
const READINESS_VISUAL = {
  "Early Stage":                           { badge: "bg-slate-100 text-slate-600",     label: "EARLY STAGE"                         },
  "Finance Ready (Subject to Validation)": { badge: "bg-emerald-100 text-emerald-800", label: "FINANCE READY (SUBJECT TO VALIDATION)" },
};

// ── Progression answers ─────────────────────────────────────────────────────
function deriveProgressionAnswers(reviewStatus, isBankable) {
  return {
    isUsable: reviewStatus !== "Action Required",
    isFinanceable: isBankable,
    nextStep: reviewStatus === "Action Required"
      ? "Upload missing evidence and resubmit for review"
      : reviewStatus === "In Review"
      ? "Await reviewer decision"
      : "Engage lenders and carbon buyers",
  };
}

// ── Cache ───────────────────────────────────────────────────────────────────
let _cachedJourney = null;
let _cachedAt      = 0;
const CACHE_TTL_MS = 30000;

export function useHousingStatus() {
  const [journey, setJourney] = useState(_cachedJourney);
  const [loading, setLoading] = useState(!_cachedJourney);

  useEffect(() => {
    const now = Date.now();
    if (_cachedJourney && (now - _cachedAt) < CACHE_TTL_MS) {
      setJourney(_cachedJourney);
      setLoading(false);
      return;
    }
    client.get("/housing/my-journey").then(r => {
      _cachedJourney = r.data;
      _cachedAt      = Date.now();
      setJourney(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const wfState  = journey?.workflow_status || journey?.status || "calculated";
  const fields   = STATUS_MAP[wfState] || STATUS_MAP.calculated;
  const visual   = STATUS_VISUAL[fields.review_status] || STATUS_VISUAL["In Review"];
  const readVis  = READINESS_VISUAL[fields.readiness]  || READINESS_VISUAL["Early Stage"];
  const isBankable     = deriveBankable(fields);
  const progression    = deriveProgressionAnswers(fields.review_status, isBankable);

  return {
    loading,
    journey,
    wfState,
    // Decision-grade labels
    review_status:  fields.review_status,
    stage:          fields.stage,
    readiness:      isBankable ? "Finance Ready (Subject to Validation)" : fields.readiness,
    // Visual helpers
    badgeClass:     visual.badge,
    dotClass:       visual.dot,
    ringClass:      visual.ring,
    statusIcon:     visual.icon,
    readinessBadge: readVis.badge,
    readinessLabel: readVis.label,
    // Derived booleans
    isActionRequired: fields.review_status === "Action Required",
    isInReview:       fields.review_status === "In Review",
    isVerified:       fields.review_status === "Verified",
    isBankable,
    // Progression answers
    isUsable:       progression.isUsable,
    isFinanceable:  progression.isFinanceable,
    nextStep:       progression.nextStep,
  };
}
